import styled from "@emotion/styled"
import { Box, Button } from "@mui/material"
import {
  CalendarIcon,
  CaseIcon,
  CheckCircleIcon,
  Container,
  CustomBreadcrumbs,
  Desktop,
  LocationIcon,
  Mobile,
  NewText,
  ShareIcon,
} from "ui"
import { sharedColors as colors } from "ui/theme/colors"
import { extractProperty, Job, replaceRichTextLinks } from "utils"
import { convertISODateString } from "utils/utils"
import FormSection from "./FormSection"

const StyledBreadCrumbs = styled.div`
  padding-top: 32px;
  padding-bottom: 40px;
  ${(props) => props.theme.breakpoints.up("md")} {
    padding-top: 40px;
  }
`

const StyledGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin: 24px 0px 16px;
  max-width: 750px;
  ${(props) => props.theme.breakpoints.up("md")} {
    margin: 32px 0px;
    width: fit-content;
  }
`
const StyledBox = styled(Box)`
  justify-self: baseline;
  margin-bottom: 40px;
  ${(props) => props.theme.breakpoints.up("md")} {
    grid-column: 1 / 2;
    grid-row: 1 / 3;
    margin-bottom: 60px;
  }
`
const StyledItem = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  ${(props) => props.theme.breakpoints.up("md")} {
    min-width: 300px;
  }
`

const StyledButton = styled(Button)`
  min-width: 153px;
  height: 48px;
  padding: 11px 22px;
  border: none;
  border-radius: 200px;
  outline: none;
  font-size: 18px;
  font-weight: 600;
  text-decoration: none;
  width: auto;
  background-color: ${(p) => p.theme.colors.buttonActiveBackgroundColor};
  color: ${(p) => p.theme.colors.buttonHoverTextColor};
  box-shadow: 0px 4px 8px 0px #00000029;
  display: flex;
  gap: 8px;
  justify-content: space-between;
  :hover {
    background-color: ${(p) => p.theme.colors.buttonActiveBackgroundColor};
    color: ${(p) => p.theme.colors.buttonHoverTextColor};
  }
`

const StyledShareButton = styled(StyledButton)`
  padding: 8px 16px;
  background-color: transparent;
  height: 42px;
  color: ${(p) => p.theme.colors.black};
  :hover {
    background-color: transparent;
    color: ${(p) => p.theme.colors.black};
  }
`

const StyledContent = styled.div`
  font-size: 18px !important;

  strong,
  b {
    font-size: 20px !important;
    font-weight: 700;
  }

  p,
  li,
  span,
  div {
    font-size: 18px !important;
  }

  &:first-child {
    margin-top: 0;
  }
  &:last-child {
    margin-bottom: 0;
  }
`

function TopContent({ job, messages, data }: { job: Job; messages?: any; data?: any }) {
  const dataToRender = data?.page
  const jobDisclaimerText = extractProperty(dataToRender, "jobDisclaimerText")

  const onShare = async () => {
    const shareData = {
      title: job?.jobName || "",
      url: window.location.href,
    }
    if (navigator.share && navigator.canShare(shareData)) {
      try {
        await navigator.share(shareData)
      } catch (err) {
        console.error(err)
      }
    } else {
      navigator.clipboard.writeText(shareData.url)
    }
  }
  const subtitle = job?.professionName
  const fields = [job?.professionName, job?.subfield]
    ?.filter((field) => Boolean(field))
    ?.join(" | ")

  const position = job?.jobName
  const scopeJobName = job?.scopeJobName
  const content = job?.description
  const location = job?.areaName
  const date = convertISODateString(job?.startAdvertisingDate)
  const jobNumber = `מספר משרה - ${job?.orderId}`

  // const locale = useLocale()NOTE: Removed as per request, leaving for now in case of future need

  // const scrollToForm = () => {NOTE: Removed as per request, leaving for now in case of future need
  //   const target = document.getElementById("target-section")
  //   const top = (target?.offsetTop || 0) - 200
  //   window.scrollTo({ top, behavior: "smooth" })
  // }

  return (
    <Container>
      <StyledBreadCrumbs>
        <CustomBreadcrumbs
          current={{ title: "משרה", url: `/` }}
          noPadding={true}
          parents={[
            { title: "ראשי", url: "/" },
            { title: "קריירה", url: "/career" },
          ]}
          desktopFontSize={18}
          desktopLineHeight="100%"
          mobileFontSize={16}
          mobileLineHeight="100%"
        />
      </StyledBreadCrumbs>
      <Box display={"flex"} justifyContent={"space-between"} alignItems={"flex-start"}>
        <Box>
          <StyledItem>
            <NewText
              component="p"
              desktopFontSize={18}
              desktopLineHeight="100%"
              mobileFontSize={14}
              mobileLineHeight="20px"
              color={colors.gray}
            >
              {jobNumber}
            </NewText>
          </StyledItem>
          <NewText
            component="h1"
            desktopFontSize={40}
            desktopLineHeight="44px"
            desktopFontWeight={"700"}
            mobileFontSize={32}
            mobileLineHeight="36px"
            color={colors.corpBlue}
          >
            {position}
          </NewText>
          {/* <Desktop> NOTE: Removed as per request, leaving for now in case of future need
            <StyledButton onClick={scrollToForm} sx={{ marginTop: "16px" }}>
              {locale === "he" ? "הגשת מועמדות" : "Apply Now"}
            </StyledButton>
          </Desktop> */}
        </Box>
        <Desktop>
          <StyledShareButton onClick={onShare}>
            <ShareIcon width={20} height={20} />
            {messages?.sharePos ? messages?.sharePos : "שיתוף משרה"}
          </StyledShareButton>
        </Desktop>
      </Box>
      <StyledBox>
        <Box>
          <StyledGrid>
            {subtitle && (
              <StyledItem>
                <CaseIcon width={16} height={16} />
                <NewText
                  component="p"
                  desktopFontSize={18}
                  desktopLineHeight="26px"
                  mobileFontSize={16}
                  mobileLineHeight="23px"
                  color={colors.black}
                >
                  {fields}
                </NewText>
              </StyledItem>
            )}
            {scopeJobName && (
              <StyledItem>
                <CalendarIcon width={16} height={16} />
                <NewText
                  component="p"
                  desktopFontSize={18}
                  desktopLineHeight="26px"
                  mobileFontSize={16}
                  mobileLineHeight="23px"
                  color={colors.black}
                >
                  {scopeJobName}
                </NewText>
              </StyledItem>
            )}
            {location && (
              <StyledItem>
                <LocationIcon width={16} height={16} />
                <NewText
                  component="p"
                  desktopFontSize={18}
                  desktopLineHeight="26px"
                  mobileFontSize={16}
                  mobileLineHeight="23px"
                  color={colors.black}
                >
                  {location}
                </NewText>
              </StyledItem>
            )}
            {date && (
              <StyledItem>
                <CheckCircleIcon width={16} height={16} />
                <NewText
                  component="p"
                  desktopFontSize={18}
                  desktopLineHeight="26px"
                  mobileFontSize={16}
                  mobileLineHeight="23px"
                  color={colors.black}
                >
                  {date}
                </NewText>
              </StyledItem>
            )}
          </StyledGrid>
          <Mobile>
            <StyledShareButton
              onClick={onShare}
              sx={{
                marginBottom: "32px",
              }}
            >
              <ShareIcon width={20} height={20} />
              {messages?.sharePos ? messages?.sharePos : "שיתוף משרה"}
            </StyledShareButton>
          </Mobile>

          {content && (
            <StyledContent dangerouslySetInnerHTML={{ __html: replaceRichTextLinks(content) }} />
          )}
          {jobDisclaimerText && (
            <NewText
              component="p"
              desktopFontSize={14}
              mobileFontSize={14}
              color={colors.black}
              marginTop={"16px"}
            >
              {jobDisclaimerText}
            </NewText>
          )}
        </Box>
      </StyledBox>
      <FormSection
        orderId={job?.orderId}
        jobType={job?.jobName}
        messages={messages}
        isHot={job?.isHot === 1}
      />
    </Container>
  )
}

export default TopContent
